import { Anchor, Box, Heading, Popover, PropertyCard, Tag, Text } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import SelectPropertyConstants from '../../../constants/SelectPropertyConstants';

import {
  emitSelectLeasePropertyEvent,
  SELECT_LEASE_PROPERTY_EVENT_ID,
} from '../../../analytics/events/selectPropertyPage';

import type { ValueOf } from 'type-fest';
import type { SelectPropertyListing } from '../types';

type Props = {
  type: ValueOf<typeof SelectPropertyConstants>;
  listing: SelectPropertyListing;
};

const SelectPropertyCard = (props: Props) => {
  const { listing, type } = props;
  const { listingAlias, city, message, state, street, unit, zip, primaryPhotoUrl, urlPath, eligibility } = listing;
  const { isEligible, title: ineligibleTitle, description: ineligibleDescription } = eligibility;

  const { formattedStreet, formattedCityStateZip } = propertyUtils.formatAddress({ street, city, state, unit, zip });

  const { refetch: fetchWebAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    { enabled: false },
  );

  const renderStatus = () => {
    switch (type) {
      case SelectPropertyConstants.LISTINGS:
        return (
          <div>
            <Box
              css={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: message === 'Listed for rent' ? 'bg.accent.green.hero' : 'gray.400',
                marginRight: '200',
              }}
            />
            <Box css={{ display: 'inline-block' }}>{message}</Box>
          </div>
        );

      // fall through
      case SelectPropertyConstants.APPLICATIONS:
      case SelectPropertyConstants.LEASES:
      case SelectPropertyConstants.PAYMENTS:
        return '';
      default:
        console.warn('No Case for: ', type);
        break;
    }
  };

  const renderPhotoBody = () => <PropertyCard.Photo src={primaryPhotoUrl} alt={primaryPhotoUrl ? 'hero' : 'house'} />;

  const handleCardClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (type === SelectPropertyConstants.LEASES) {
      event.preventDefault();
      const { data } = await fetchWebAnalyticsData();
      emitSelectLeasePropertyEvent(data?.lease);
      window.location.href = urlPath;
    }
  };

  const IneligibleCard = () => {
    return (
      <PropertyCard
        appearance="horizontal"
        css={{
          height: '100%',
          // eslint-disable-next-line curly-quotes/no-straight-quotes
          '& > [data-c11n-component="PropertyCard.Body"]': {
            height: '100%',
          },
          // eslint-disable-next-line curly-quotes/no-straight-quotes
          '& > [data-c11n-component="PropertyCard.Body"] > [data-c11n-component="PropertyCard.PhotoWrapper"] > [data-c11n-component="PropertyCard.PhotoBody"]':
            {
              minHeight: 'auto',
            },
          // eslint-disable-next-line curly-quotes/no-straight-quotes
          '& > [data-c11n-component="PropertyCard.Body"] > [data-c11n-component="PropertyCard.PhotoWrapper"]': {
            filter: 'brightness(0.5)',
          },
        }}
        data={{
          dataArea1: {
            children: (
              <>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                  <Tag size="sm"> {ineligibleTitle} </Tag>
                  <Popover
                    body={<Text>{ineligibleDescription}</Text>}
                    header={<Heading level={6}>{ineligibleTitle}</Heading>}
                    trigger={<Popover.Trigger />}
                  />
                </Box>
                <Text
                  textStyle="body-lg"
                  css={{
                    fontWeight: 'bold',
                    color:
                      'gray.400' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                  }}
                >
                  {formattedStreet}
                </Text>
              </>
            ),
            lineClamp: 'none',
          },
          dataArea2: {
            children: (
              <Text
                css={{
                  color:
                    'gray.400' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                }}
              >
                {formattedCityStateZip}
              </Text>
            ),
            lineClamp: 'none',
          },
        }}
        photoBody={renderPhotoBody()}
      />
    );
  };

  const EligibleCardContent = () => (
    <PropertyCard
      appearance="horizontal"
      interactive={true}
      css={{
        height: '100%',
        // eslint-disable-next-line curly-quotes/no-straight-quotes
        '& > [data-c11n-component="PropertyCard.Body"]': {
          height: '100%',
        },

        minHeight: '86px',
        // eslint-disable-next-line curly-quotes/no-straight-quotes
        '& > [data-c11n-component="PropertyCard.Body"] > [data-c11n-component="PropertyCard.PhotoWrapper"] > [data-c11n-component="PropertyCard.PhotoBody"]':
          {
            minHeight: 'auto',
          },
        // eslint-disable-next-line curly-quotes/no-straight-quotes
        '& > [data-c11n-component="PropertyCard.Body"]:hover': {
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.2)',
        },
      }}
      data={{
        dataArea1: { children: <Text textStyle="body-lg-bold">{formattedStreet}</Text>, lineClamp: 'none' },
        dataArea2: { children: <Text>{formattedCityStateZip}</Text>, lineClamp: 'none' },
        dataArea4: renderStatus(),
      }}
      photoBody={renderPhotoBody()}
    />
  );

  const EligibleCard = () => {
    const escapeUrl = type !== SelectPropertyConstants.LISTINGS;

    return escapeUrl ? (
      <Anchor
        href={urlPath}
        onClick={handleCardClick}
        data-eventid={type === SelectPropertyConstants.LEASES ? SELECT_LEASE_PROPERTY_EVENT_ID : undefined}
        css={{ textDecoration: 'none !important' }}
      >
        <EligibleCardContent />
      </Anchor>
    ) : (
      <Anchor href={urlPath} onClick={handleCardClick} css={{ textDecoration: 'none !important' }}>
        <EligibleCardContent />
      </Anchor>
    );
  };

  return isEligible ? <EligibleCard /> : <IneligibleCard />;
};

export { SelectPropertyCard };
export default SelectPropertyCard;
